import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, effect, input, model, output, signal } from '@angular/core';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';

import { AuthorizationService } from '../../../../../modules/fwcore/services/authorization.service';
import { AdminService } from '../../../services/admin.service';

import { FormsModule } from '@angular/forms';
import { UserGroup } from '../../../models/user-company';
import { RequestUserGroup, ResponseUserGroup } from '../../../models/messages/user-company-messages';

@Component({
  selector: 'app-company-relation-droplist',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonsModule, DropDownListModule],
  templateUrl: './company-relation-droplist.component.html',
  styleUrl: './company-relation-droplist.component.scss'
})
export class CompanyRelationDroplistComponent implements OnInit, OnDestroy {

  public controlWidth = input<number>(200);
  public relationshipCode = model<string | null>(null);

  public heading = input<string | null>(null);
  public headingCssClass = input<string | null>(null);


  /**
   * The list of available companies to display in the drop-down.
   */
  public dataItems = model<UserGroup[]>([]);
  public selected = output<UserGroup | null>();

  //----------------- State properties  ------------------

  protected filteredDataItems = signal<UserGroup[]>([]);

  protected selectedItem = computed(() => {
    const code = this.relationshipCode();
    const items = this.dataItems();

    if (!items || items.length === 0)
      return null;

    return code ? items.find(i => i.code === code) ?? null : null;
  });

  protected loading = signal<boolean>(false);
  protected destroy$ = new Subject<void>();
  protected debounceGetData = new Subject<void>();

  //------------------- Constructor  --------------------------
  constructor(
    public serviceAuth: AuthorizationService,
    protected adminService: AdminService
  ) {
    // Debounce the get data call
    this.debounceGetData.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => this.getData());

  }

  //-------------------- ng  ----------------------------------
  ngOnInit(): void {
    this.debounceGetData.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------- Control Processing -------------
  protected loaderShow(): void {
    this.loading.set(true);
  }

  protected loaderClose(): void {
    this.loading.set(false);
  }

  // ---------------- Event Processing -------------
  public onValueChange(value: UserGroup | null): void {
    this.handleSelectionChange(value);
  }

  public onSelectionChange(value: UserGroup | null): void {
    this.handleSelectionChange(value);
  }

  private handleSelectionChange(value: UserGroup | null): void {

    const newCode = value?.code ?? null;

    // Update the model (syncs with parent)
    this.relationshipCode.set(newCode);      

    // Emit the selection event
    this.selected.emit(value);
  }

  public onFilterChange(filter: string): void {

    const items: UserGroup[] = this.dataItems() ?? [];

    const filterLower = (filter ?? '').toLowerCase();
    const filtered = items.filter(
      (item) => (item.name ?? '').toLowerCase().includes(filterLower)
    );

    this.filteredDataItems.set(filtered);
  }

  /**
   * Determines the initial selection based on stored value, isMain flag, or first item
   */
  protected determineInitialSelection(): string | null {

    const items = this.dataItems();
    if (!items || items.length === 0)
      return null;

    // 1. Check if parent already provided a companyId
    const code = this.relationshipCode();
    if (code && items.some(i => i.code === code)) {
      return code;
    }

    // 2. Default to first item
    return items[0]?.code ?? null;
  }

  // -------------------- API Processing --------------------------
  protected getData(): void {

    this.loaderShow();

    const req: RequestUserGroup = {
      isActive: true
    };

    this.adminService.getData$<RequestUserGroup, ResponseUserGroup>(
      req,
      this.adminService.userCompanyRelationGroupEndpoint
    )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderClose())
      )
      .subscribe((resp) => {
        if (resp.success === true) {

          const items = resp.data ?? [];
          this.dataItems.set(items);
          this.filteredDataItems.set(items);

          // Determine and set initial selection
          const initialCode = this.determineInitialSelection();

          if (initialCode) {
            this.relationshipCode.set(initialCode); 

            // Emit the computed selection
            this.selected.emit(this.selectedItem());

          }
        }
      });
  }
}

